import { useMemo, useState } from "react";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";

import { WetSignatureCaptureMethodEnums } from "@api/graphql/types-and-hooks";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import { ReportFieldDrawnSignature } from "@components/ReportField/inputs/ReportFieldDrawnSignature";
import { ReportFieldImage } from "@components/ReportField/inputs/ReportFieldImage";
import { ReportFieldTypedSignature } from "@components/ReportField/inputs/ReportFieldTypedSignature";
import {
  WetSignatureMethod,
  parseWetSignatureContent,
  serializeWetSignatureContent,
} from "@components/ReportField/inputs/wetSignatureContent";
import { ReportFieldInputProps, UpdateReportSectionFieldMutateOptions } from "@components/ReportField/types";

const METHOD_LABELS: Record<WetSignatureMethod, string> = {
  typed: "Type",
  uploaded: "Upload",
  drawn: "Draw",
};

const METHOD_TO_CAPTURE_METHOD: Record<"uploaded" | "drawn", WetSignatureCaptureMethodEnums> = {
  uploaded: WetSignatureCaptureMethodEnums.Uploaded,
  drawn: WetSignatureCaptureMethodEnums.Drawn,
};

// A single WET_SIGNATURE field whose capture UI lets the signer choose
// drawn, typed, or uploaded-image at fill time (proposal.md), rather than a
// template author pre-selecting a separate field per method. Composes the
// three existing capture behaviors as sub-widgets instead of duplicating
// their internals (design.md's Migration Plan step 4 of
// `unify-wet-signature-field-type`).
export const ReportFieldWetSignature: React.FC<ReportFieldInputProps> = (props) => {
  const { field, value, onChange, disabled } = props;
  // `value` (when present) is the wrapper's live optimistic content —
  // freshly-composed envelopes from this component's own `onChange` calls
  // flow back in through it before any server round-trip completes.
  // `field.content` is only the last-fetched value, so prefer `value` and
  // fall back to it for the initial render / disabled display.
  const contentToParse = value ?? field.content;
  const parsed = useMemo(() => parseWetSignatureContent(contentToParse), [contentToParse]);
  const { allowedMethods } = parsed;

  // Switching methods before saving discards the unsaved other method's
  // draft state (design.md Decision 5): the drawn canvas holds its strokes
  // locally until its autosave starts (PROA-2913 replaced the explicit
  // "Save" link), and the upload widget holds a pending file pick until its
  // upload — conditionally unmounting the non-selected sub-widget below
  // resets that local state on switch. Typed text streams to `onChange` on
  // every keystroke, so it has no separate draft to lose.
  const [selectedMethod, setSelectedMethod] = useState<WetSignatureMethod>(parsed.method ?? allowedMethods[0]);

  if (disabled) {
    if (parsed.method === "typed") {
      return <ReportFieldTypedSignature {...props} value={parsed.value ?? ""} />;
    }
    if (parsed.method === "uploaded" || parsed.method === "drawn") {
      // `field.imageSignedUrl` is already resolved server-side for
      // WET_SIGNATURE fields (resolve_image_signed_url, task 3.2) — reuse
      // ReportFieldImage's disabled-branch rendering as-is.
      return <ReportFieldImage {...props} />;
    }
    return <ReportFieldDisabledValue />;
  }

  // Wraps the sub-widget's bare-string onChange (used for the typed text
  // input and for the uploaded/drawn "clear"/"redraw" actions) into the
  // WET_SIGNATURE content envelope, preserving `allowedMethods` and this
  // method's slot. A cleared value (empty string) drops `method`/`value`
  // entirely rather than recording an empty one, matching the
  // "unfilled" template-only shape (design.md Decision 4).
  const composeOnChange =
    (method: WetSignatureMethod) => (content: string, mutateOptions?: UpdateReportSectionFieldMutateOptions) =>
      onChange(
        content === ""
          ? serializeWetSignatureContent(allowedMethods)
          : serializeWetSignatureContent(allowedMethods, method, content),
        mutateOptions,
      );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2" role="tablist" aria-label="Signature method">
        {allowedMethods.map((method) => (
          <button
            key={method}
            type="button"
            role="tab"
            aria-selected={selectedMethod === method}
            className={cn(
              "rounded-full px-3 py-1 text-sm",
              selectedMethod === method
                ? "bg-limestone-500 text-white"
                : "bg-limestone-200 text-text-secondary hover:bg-limestone-100",
            )}
            onClick={() => setSelectedMethod(method)}
          >
            {METHOD_LABELS[method]}
          </button>
        ))}
      </div>

      {selectedMethod === "typed" && (
        <ReportFieldTypedSignature
          {...props}
          value={parsed.method === "typed" ? parsed.value ?? "" : ""}
          onChange={composeOnChange("typed")}
        />
      )}
      {selectedMethod === "uploaded" && (
        <ReportFieldImage
          {...props}
          captureMethod={METHOD_TO_CAPTURE_METHOD.uploaded}
          onChange={composeOnChange("uploaded")}
        />
      )}
      {selectedMethod === "drawn" && (
        <ReportFieldDrawnSignature
          {...props}
          captureMethod={METHOD_TO_CAPTURE_METHOD.drawn}
          onChange={composeOnChange("drawn")}
        />
      )}
    </div>
  );
};
